import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    Eye,
    EyeOff,
    Newspaper,
    Calendar,
    Image as ImageIcon,
    X,
    Loader2,
    Save,
    Type,
    Layers,
    GalleryHorizontal,
    Bold,
    Italic
} from 'lucide-react';
import Toast from '../Common/Toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import './NewsManager.css';
import API_BASE_URL from '../../config/api';

const NewsManager = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingNews, setEditingNews] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: null });

    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        isPublished: false,
        publishedAt: '',
        image: null,
        galleryImages: []
    });
    const [previewUrl, setPreviewUrl] = useState(null);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const contentRef = useRef(null);
    const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false });

    const categories = [
        'Clube', 'Atletismo', 'Badminton', 'Basquetebol',
        'Futevólei', 'Futsal', 'Hóquei em Patins',
        'Ténis de Mesa', 'Voleibol'
    ];

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/news/admin/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setNews(data);
            }
        } catch (error) {
            addToast('Erro ao carregar notícias', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const token = localStorage.getItem('token');
        const formDataToSend = new FormData();

        formDataToSend.append('title', formData.title);
        formDataToSend.append('excerpt', formData.excerpt);
        // Normalize HTML: b→strong, i→em for consistent public rendering
        const rawContent = contentRef.current?.innerHTML || formData.content;
        const currentContent = rawContent
            .replace(/<b(\s[^>]*)?>/gi, '<strong$1>')
            .replace(/<\/b>/gi, '</strong>')
            .replace(/<i(\s[^>]*)?>/gi, '<em$1>')
            .replace(/<\/i>/gi, '</em>');
        formDataToSend.append('content', currentContent);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('isPublished', formData.isPublished);

        if (formData.publishedAt) {
            formDataToSend.append('publishedAt', new Date(formData.publishedAt).toISOString());
        }
        if (formData.image) {
            formDataToSend.append('image', formData.image);
        }

        if (formData.galleryImages.length > 0) {
            formData.galleryImages.forEach((image) => {
                formDataToSend.append('galleryImages', image);
            });
        }

        try {
            const url = editingNews
                ? `${API_BASE_URL}/api/news/${editingNews.id}`
                : `${API_BASE_URL}/api/news`;

            const method = editingNews ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}` },
                body: formDataToSend
            });

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchNews();
                addToast(editingNews ? 'Notícia atualizada!' : 'Notícia publicada com sucesso!');
            } else {
                addToast('Erro ao guardar notícia', 'error');
            }
        } catch (error) {
            addToast('Erro técnico ao processar formulário', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const id = confirmState.id;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/news/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchNews();
                addToast('Notícia removida do sistema', 'warning');
            } else {
                addToast('Erro ao eliminar notícia', 'error');
            }
        } catch (error) {
            addToast('Erro ao processar eliminação', 'error');
        } finally {
            setConfirmState({ isOpen: false, id: null });
        }
    };

    const handleEdit = (article) => {
        setEditingNews(article);

        // Convert UTC publishedAt to local datetime-local string
        let localPublishedAt = '';
        if (article.publishedAt) {
            const d = new Date(article.publishedAt);
            const offset = d.getTimezoneOffset();
            const local = new Date(d.getTime() - offset * 60000);
            localPublishedAt = local.toISOString().slice(0, 16);
        }

        setFormData({
            title: article.title,
            excerpt: article.excerpt,
            content: article.content,
            category: article.category,
            isPublished: article.isPublished,
            publishedAt: localPublishedAt,
            image: null,
            galleryImages: []
        });

        if (article.imageUrl) {
            setPreviewUrl(getImageUrl(article.imageUrl));
        }

        if (article.galleryImages?.length > 0) {
            setGalleryPreviews(article.galleryImages.map(img => getImageUrl(img.imageUrl)));
        } else {
            setGalleryPreviews([]);
        }

        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            title: '', excerpt: '', content: '',
            category: '', isPublished: false,
            publishedAt: '',
            image: null, galleryImages: []
        });
        setEditingNews(null);
        setPreviewUrl(null);
        setGalleryPreviews([]);
        // Clear the WYSIWYG editor
        if (contentRef.current) contentRef.current.innerHTML = '';
    };

    // Sync WYSIWYG editor content when modal opens (edit or new)
    useEffect(() => {
        if (showModal && contentRef.current) {
            contentRef.current.innerHTML = formData.content || '';
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    // Update toolbar button state when cursor moves
    useEffect(() => {
        const onSelectionChange = () => {
            if (!showModal || !contentRef.current) return;
            const sel = window.getSelection();
            if (sel && contentRef.current.contains(sel.anchorNode)) {
                checkFormatState();
            }
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    // Check bold/italic state at current cursor position
    const checkFormatState = () => {
        setActiveFormats({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
        });
    };

    // Toggle bold/italic — works for typing mode AND text selection (Word-like)
    const insertFormat = (command) => {
        contentRef.current?.focus();
        try {
            document.execCommand('styleWithCSS', false, false);
        } catch (e) {
            // fallback if styleWithCSS is not supported
        }
        document.execCommand(command, false, null);
        if (contentRef.current) {
            setFormData(prev => ({ ...prev, content: contentRef.current.innerHTML }));
        }
        setTimeout(checkFormatState, 0);
    };

    const handleContentInput = () => {
        if (contentRef.current) {
            setFormData(prev => ({ ...prev, content: contentRef.current.innerHTML }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData({ ...formData, image: file });
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleGalleryImagesChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            setFormData({ ...formData, galleryImages: [...formData.galleryImages, ...files] });
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setGalleryPreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeGalleryImage = (index) => {
        setFormData({ ...formData, galleryImages: formData.galleryImages.filter((_, i) => i !== index) });
        setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
    };

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http')) return imageUrl;
        return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Rascunho';
        return new Date(dateString).toLocaleDateString('pt-PT', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Loader2 className="spinner" size={48} />
                <p>A sincronizar base de dados de notícias...</p>
            </div>
        );
    }

    return (
        <div className="news-manager">
            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title="Eliminar Notícia"
                message="Esta ação irá remover permanentemente a notícia e toda a sua galeria associada."
                onConfirm={handleDelete}
                onCancel={() => setConfirmState({ isOpen: false, id: null })}
            />

            <header className="manager-header">
                <div className="header-content">
                    <h1>Gestão de Notícias</h1>
                    <p className="header-subtitle">Central de comunicação e comunicados oficiais</p>
                </div>
                <button className="btn-add" onClick={() => { resetForm(); setShowModal(true); }}>
                    <Plus size={20} /> Nova Notícia
                </button>
            </header>

            {news.length === 0 ? (
                <div className="empty-state glass-card">
                    <Newspaper className="empty-icon" size={64} />
                    <h3>O arquivo está vazio</h3>
                    <p>Crie o primeiro comunicado para informar os sócios</p>
                    <button className="btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={20} /> Criar Notícia
                    </button>
                </div>
            ) : (
                <div className="news-grid">
                    {news.map((article) => (
                        <div key={article.id} className="news-card">
                            <div className="news-image-wrapper">
                                {article.imageUrl ? (
                                    <img src={getImageUrl(article.imageUrl)} alt={article.title} className="news-image" />
                                ) : (
                                    <div className="news-no-image"><Newspaper size={40} /></div>
                                )}
                                <span className="news-badge">{article.category}</span>
                                <div className="news-overlay">
                                    <button className="overlay-btn edit" onClick={() => handleEdit(article)}><Pencil size={20} /></button>
                                    <button className="overlay-btn delete" onClick={() => setConfirmState({ isOpen: true, id: article.id })}><Trash2 size={20} /></button>
                                </div>
                            </div>
                            <div className="news-info">
                                <div className="news-date">
                                    <Calendar size={14} />
                                    <span>{formatDate(article.publishedAt)}</span>
                                </div>
                                <h3>{article.title}</h3>
                                <p className="news-excerpt">{article.excerpt}</p>
                                <div className={`news-status ${article.isPublished ? 'status-active' : 'status-inactive'}`}>
                                    {article.isPublished ? <Eye size={12} /> : <EyeOff size={12} />}
                                    <span>{article.isPublished ? 'Público' : 'Rascunho'}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm(); } }}>
                    <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <div>
                                <h2>{editingNews ? 'Editar Conteúdo' : 'Novo Comunicado'}</h2>
                                <p className="header-subtitle">Ajuste o conteúdo e as imagens da publicação</p>
                            </div>
                            <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}><X size={24} /></button>
                        </header>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-grid-layout">
                                <div className="form-main-column">
                                    <div className="form-section-group">
                                        <div className="section-label"><Type size={16} /><span>Conteúdo Escrito</span></div>
                                        <div className="form-section">
                                            <label className="form-label">Título da Notícia</label>
                                            <input
                                                type="text" className="form-input" value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Ex: CDP conquista troféu nacional..." required
                                            />
                                        </div>
                                        <div className="form-section">
                                            <label className="form-label">Resumo (Excerto)</label>
                                            <textarea
                                                className="form-textarea" value={formData.excerpt}
                                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                                placeholder="Breve introdução..." rows="2" maxLength="500" required
                                            />
                                            <small className="char-count">{formData.excerpt.length}/500</small>
                                        </div>
                                        <div className="form-section">
                                            <label className="form-label">Corpo do Artigo</label>
                                            <div className="editor-toolbar">
                                                <button
                                                    type="button"
                                                    className={`toolbar-btn${activeFormats.bold ? ' active' : ''}`}
                                                    title="Negrito"
                                                    onMouseDown={(e) => { e.preventDefault(); insertFormat('bold'); }}
                                                >
                                                    <Bold size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`toolbar-btn${activeFormats.italic ? ' active' : ''}`}
                                                    title="Itálico"
                                                    onMouseDown={(e) => { e.preventDefault(); insertFormat('italic'); }}
                                                >
                                                    <Italic size={14} />
                                                </button>
                                            </div>
                                            <div
                                                ref={contentRef}
                                                contentEditable
                                                suppressContentEditableWarning
                                                className="form-textarea content-editor wysiwyg-editor"
                                                onInput={handleContentInput}
                                                data-placeholder="Escreva aqui a notícia completa..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-side-column">
                                    <div className="form-section-group">
                                        <div className="section-label"><Layers size={16} /><span>Configuração</span></div>
                                        <div className="form-section">
                                            <label className="form-label">Categoria</label>
                                            <select
                                                className="form-input" value={formData.category}
                                                onChange={(e) => setFormData({ ...formData, category: e.target.value })} required
                                            >
                                                <option value="">Selecionar...</option>
                                                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-section">
                                            <label className="form-label">Visibilidade</label>
                                            <label className="toggle-switch premium-toggle">
                                                <input
                                                    type="checkbox" checked={formData.isPublished}
                                                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                                                />
                                                <span className="toggle-slider"></span>
                                                <span className="toggle-label">{formData.isPublished ? 'Publicar Agora' : 'Manter em Rascunho'}</span>
                                            </label>
                                        </div>
                                        {formData.isPublished && (
                                            <div className="form-section">
                                                <label className="form-label"><Calendar size={14} /> Data e Hora de Publicação</label>
                                                <input
                                                    type="datetime-local"
                                                    className="form-input"
                                                    value={formData.publishedAt}
                                                    onChange={(e) => setFormData({ ...formData, publishedAt: e.target.value })}
                                                />
                                                <small className="char-count">Deixar vazio para usar a hora atual</small>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-section-group">
                                        <div className="section-label"><ImageIcon size={16} /><span>Capa Principal</span></div>
                                        <div className="image-upload-area">
                                            {previewUrl ? (
                                                <div className="image-preview premium-preview small">
                                                    <img src={previewUrl} alt="Preview" />
                                                    <div className="preview-overlay">
                                                        <button type="button" className="btn-change-image" onClick={() => document.getElementById('imageInput').click()}><Pencil size={14} /></button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label htmlFor="imageInput" className="upload-label premium-label small">
                                                    <Plus size={24} />
                                                    <span>Upload Capa</span>
                                                </label>
                                            )}
                                            <input id="imageInput" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                                        </div>
                                    </div>

                                    <div className="form-section-group">
                                        <div className="section-label"><GalleryHorizontal size={16} /><span>Galeria</span></div>
                                        <div className="gallery-manager">
                                            <div className="gallery-mini-grid">
                                                {galleryPreviews.map((preview, index) => (
                                                    <div key={index} className="gallery-item-preview">
                                                        <img src={preview} alt="Gallery" />
                                                        <button type="button" className="remove-btn" onClick={() => removeGalleryImage(index)}><X size={12} /></button>
                                                    </div>
                                                ))}
                                                <button type="button" className="add-gallery-btn" onClick={() => document.getElementById('galleryInput').click()}>
                                                    <Plus size={20} />
                                                </button>
                                            </div>
                                            <input id="galleryInput" type="file" accept="image/*" multiple onChange={handleGalleryImagesChange} style={{ display: 'none' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <footer className="modal-actions premium-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</button>
                                <button type="submit" className="btn-submit premium-submit" disabled={submitting}>
                                    {submitting ? <Loader2 className="spinner" size={18} /> : (editingNews ? 'Atualizar Notícia' : 'Publicar Agora')}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsManager;